import React, { useState, useEffect } from "react";
import { db, storage } from "./firebaseConfig";
import {
  collection,
  getDocs,
  addDoc,
  Timestamp,
  doc,
  deleteDoc,
  query,
  where,
} from "firebase/firestore";
import { ref, uploadBytes, getDownloadURL } from "firebase/storage";
import { useNavigate, Link } from "react-router-dom";
import "./CreateQuestions.css";

const CreateQuestions = () => {
  const [subjects, setSubjects] = useState([]);
  const [selectedSubject, setSelectedSubject] = useState("");
  const [questions, setQuestions] = useState([]);
  const [filteredQuestions, setFilteredQuestions] = useState([]);
  const [filters, setFilters] = useState({
    topic: "",
    level: "",
    specCode: "",
  });
  const [showForm, setShowForm] = useState(false);
  const [newQuestion, setNewQuestion] = useState({
    questionText: "",
    markSchemeAnswer: "",
    maxMark: "",
    topic: "",
    subject: "",
    level: "",
    specCode: "",
    textBoxSize: "medium",
  });

  const [questionImage, setQuestionImage] = useState(null);
  const [markSchemeImage, setMarkSchemeImage] = useState(null);
  const [questionImageURL, setQuestionImageURL] = useState("");
  const [markSchemeImageURL, setMarkSchemeImageURL] = useState("");
  const [selectedQuestionIds, setSelectedQuestionIds] = useState([]);
  const [isSubmitting, setIsSubmitting] = useState(false);

  const navigate = useNavigate();

  // ─────────────────────────────────────────────────────────────────────────────
  // Fetch subjects on mount
  // ─────────────────────────────────────────────────────────────────────────────
  const fetchSubjects = async () => {
    try {
      const querySnapshot = await getDocs(collection(db, "questions"));
      const subjectsSet = new Set();
      querySnapshot.forEach((doc) => {
        const subject = doc.data().subject;
        if (subject) subjectsSet.add(subject);
      });
      setSubjects(Array.from(subjectsSet).sort());
    } catch (error) {
      console.error("Error fetching subjects: ", error.message);
    }
  };

  // Fetch questions for selected subject
  const fetchQuestionsBySubject = async (subject) => {
    try {
      const q = query(
        collection(db, "questions"),
        where("subject", "==", subject)
      );
      const querySnapshot = await getDocs(q);
      const fetchedQuestions = querySnapshot.docs.map((docSnap) => ({
        id: docSnap.id,
        ...docSnap.data(),
      }));
      setQuestions(fetchedQuestions);
      setFilteredQuestions(fetchedQuestions);
    } catch (error) {
      console.error("Error fetching questions: ", error.message);
    }
  };

  useEffect(() => {
    fetchSubjects();
  }, []);

  // Fetch questions when subject is selected
  useEffect(() => {
    if (selectedSubject) {
      fetchQuestionsBySubject(selectedSubject);
    }
  }, [selectedSubject]);

  // Add ripple effect to subject buttons
  useEffect(() => {
    // Add ripple effect to subject buttons
    const buttons = document.querySelectorAll('.subject-button');
    
    const handleRippleEffect = function(e) {
      const x = e.clientX - e.target.getBoundingClientRect().left;
      const y = e.clientY - e.target.getBoundingClientRect().top;
      
      const ripple = document.createElement('span');
      ripple.className = 'ripple-effect';
      ripple.style.left = `${x}px`;
      ripple.style.top = `${y}px`;
      
      this.appendChild(ripple);
      
      setTimeout(() => {
        ripple.remove();
      }, 600);
    };
    
    buttons.forEach(button => {
      button.addEventListener('click', handleRippleEffect);
    });
    
    return () => {
      buttons.forEach(button => {
        button.removeEventListener('click', handleRippleEffect);
      });
    };
  }, [subjects]); // Re-run when subjects array changes

  // ─────────────────────────────────────────────────────────────────────────────
  // Handlers
  // ─────────────────────────────────────────────────────────────────────────────
  const handleInputChange = (e) => {
    const { name, value } = e.target;
    setNewQuestion((prev) => ({ ...prev, [name]: value }));
  };

  // Handle subject selection with toggle behavior
  const handleSubjectSelect = (subject) => {
    // If clicking the already selected subject, deselect it
    if (selectedSubject === subject) {
      setSelectedSubject("");
      setQuestions([]);
      setFilteredQuestions([]);
      // Reset filters when clearing subject
      setFilters({ topic: "", level: "", specCode: "" });
    } else {
      // Select the new subject
      setSelectedSubject(subject);
      // Reset filters when changing subject
      setFilters({ topic: "", level: "", specCode: "" });
      // Fetch questions for the selected subject
      fetchQuestionsBySubject(subject);
    }
  };

  const handleAddQuestion = async (e) => {
    e.preventDefault();
    setIsSubmitting(true);

    // Validate required fields
    if (
      !newQuestion.maxMark ||
      !newQuestion.topic ||
      !newQuestion.subject ||
      !newQuestion.level ||
      !newQuestion.specCode
    ) {
      alert("Max Mark, Topic, Subject, Level, and Spec Code are required!");
      setIsSubmitting(false);
      return;
    }

    // For the question field, require either text OR an uploaded image.
    if (!newQuestion.questionText && !questionImageURL) {
      alert("Please provide either a question text or upload a question image.");
      setIsSubmitting(false);
      return;
    }

    // For the mark scheme field, require either text OR an uploaded image.
    if (!newQuestion.markSchemeAnswer && !markSchemeImageURL) {
      alert("Please provide either a mark scheme text or upload a mark scheme image.");
      setIsSubmitting(false);
      return;
    }

    try {
      const questionData = {
        ...newQuestion,
        maxMark: parseInt(newQuestion.maxMark, 10),
        createdAt: Timestamp.now(),
        updatedAt: Timestamp.now(),
        questionText: newQuestion.questionText || "",
        markSchemeAnswer: newQuestion.markSchemeAnswer || "",
      };

      // Include image URLs if they exist
      if (questionImageURL) {
        questionData.questionImageURL = questionImageURL;
      }
      if (markSchemeImageURL) {
        questionData.markSchemeImageURL = markSchemeImageURL;
      }

      await addDoc(collection(db, "questions"), questionData);

      // Success toast notification instead of alert
      const toast = document.createElement("div");
      toast.className = "toast success";
      toast.textContent = "Question added successfully!";
      document.body.appendChild(toast);
      setTimeout(() => document.body.removeChild(toast), 3000);

      // If the added question matches the currently selected subject, refresh the questions
      if (questionData.subject === selectedSubject) {
        fetchQuestionsBySubject(selectedSubject);
      }
      
      resetForm();
    } catch (error) {
      console.error("Error adding question: ", error.message);
      alert("Error adding question. Please try again.");
    } finally {
      setIsSubmitting(false);
    }
  };

  const resetForm = () => {
    setNewQuestion({
      questionText: "",
      markSchemeAnswer: "",
      maxMark: "",
      topic: "",
      subject: "",
      level: "",
      specCode: "",
      textBoxSize: "medium",
    });
    setQuestionImage(null);
    setMarkSchemeImage(null);
    setQuestionImageURL("");
    setMarkSchemeImageURL("");
    setShowForm(false);
  };

  const handleFilterChange = (e) => {
    const { name, value } = e.target;
    const updatedFilters = { ...filters, [name]: value };
    setFilters(updatedFilters);

    const filtered = questions.filter((question) => {
      const topicMatch =
        !updatedFilters.topic ||
        question.topic.toLowerCase().includes(updatedFilters.topic.toLowerCase());

      const levelMatch =
        !updatedFilters.level ||
        question.level.toLowerCase().includes(updatedFilters.level.toLowerCase());

      const specMatch =
        !updatedFilters.specCode ||
        question.specCode.toLowerCase().includes(updatedFilters.specCode.toLowerCase());

      return topicMatch && levelMatch && specMatch;
    });
    setFilteredQuestions(filtered);
  };

  const clearFilters = () => {
    setFilters({ topic: "", level: "", specCode: "" });
    setFilteredQuestions(questions);
  };

  // Handle checkbox select/deselect for manual selection
  const handleCheckboxChange = (questionId) => {
    setSelectedQuestionIds((prevSelected) => {
      if (prevSelected.includes(questionId)) {
        return prevSelected.filter((id) => id !== questionId);
      } else {
        return [...prevSelected, questionId];
      }
    });
  };

  // Delete the selected questions from Firestore
  const handleDeleteSelected = async () => {
    if (selectedQuestionIds.length === 0) {
      alert("No questions selected to delete.");
      return;
    }

    const confirmed = window.confirm(
      `Are you sure you want to delete ${selectedQuestionIds.length} question(s)?`
    );
    if (!confirmed) return;

    try {
      await Promise.all(
        selectedQuestionIds.map(async (questionId) => {
          await deleteDoc(doc(db, "questions", questionId));
        })
      );

      const updatedQuestions = questions.filter(
        (q) => !selectedQuestionIds.includes(q.id)
      );
      setQuestions(updatedQuestions);
      setFilteredQuestions(updatedQuestions);
      setSelectedQuestionIds([]);

      // Success toast notification
      const toast = document.createElement("div");
      toast.className = "toast success";
      toast.textContent = `${selectedQuestionIds.length} question(s) deleted successfully!`;
      document.body.appendChild(toast);
      setTimeout(() => document.body.removeChild(toast), 3000);
    } catch (error) {
      console.error("Error deleting questions: ", error.message);
      alert("Error deleting questions. Please try again.");
    }
  };

  // ─────────────────────────────────────────────────────────────────────────────
  // Image Upload Functions
  // ─────────────────────────────────────────────────────────────────────────────
  const handleQuestionFileChange = (e) => {
    if (e.target.files[0]) {
      setQuestionImage(e.target.files[0]);
    }
  };

  const handleMarkSchemeFileChange = (e) => {
    if (e.target.files[0]) {
      setMarkSchemeImage(e.target.files[0]);
    }
  };

  const uploadQuestionImage = async () => {
    if (!questionImage) return;
    try {
      const storageRef = ref(
        storage,
        `questions/${Date.now()}_${questionImage.name}`
      );
      await uploadBytes(storageRef, questionImage);
      const url = await getDownloadURL(storageRef);
      setQuestionImageURL(url);

      // Success toast notification
      const toast = document.createElement("div");
      toast.className = "toast success";
      toast.textContent = "Question image uploaded successfully!";
      document.body.appendChild(toast);
      setTimeout(() => document.body.removeChild(toast), 3000);
    } catch (error) {
      console.error("Error uploading question image:", error.message);
      alert("Error uploading question image. Please try again.");
    }
  };

  const uploadMarkSchemeImage = async () => {
    if (!markSchemeImage) return;
    try {
      const storageRef = ref(
        storage,
        `markschemes/${Date.now()}_${markSchemeImage.name}`
      );
      await uploadBytes(storageRef, markSchemeImage);
      const url = await getDownloadURL(storageRef);
      setMarkSchemeImageURL(url);

      // Success toast notification
      const toast = document.createElement("div");
      toast.className = "toast success";
      toast.textContent = "Mark scheme image uploaded successfully!";
      document.body.appendChild(toast);
      setTimeout(() => document.body.removeChild(toast), 3000);
    } catch (error) {
      console.error("Error uploading mark scheme image:", error.message);
      alert("Error uploading mark scheme image. Please try again.");
    }
  };

  // ─────────────────────────────────────────────────────────────────────────────
  // Render
  // ─────────────────────────────────────────────────────────────────────────────
  return (
    <div className="question-manager-container">
      {/* Header */}
      <div className="header1">
        <h1>Question Manager</h1>
      </div>

      {/* Action Buttons Row */}
      <div className="action-button-row">
        {/* Back Button */}
        <button
          className="back-button"
          onClick={() => navigate("/dashboard")}
        >
          ← Back to Dashboard
        </button>

        {/* Add/Cancel Button */}
        <button
          onClick={() => setShowForm(!showForm)}
          className={`action-button ${showForm ? 'cancel' : ''}`}
        >
          {showForm ? "Cancel" : "Add New Question"}
        </button>
      </div>

      {/* Form Section */}
      {showForm && (
        <div className="form-container">
          <h2 className="form-title">Add New Question</h2>
          <form onSubmit={handleAddQuestion}>
            <div className="form-grid">
              <div>
                <label className="form-label">
                  Question Text
                </label>
                <textarea
                  name="questionText"
                  placeholder="Enter question text here (Optional if image uploaded)"
                  value={newQuestion.questionText}
                  onChange={handleInputChange}
                  className="textarea-field"
                />
              </div>

              <div>
                <label className="form-label">
                  Mark Scheme Answer
                </label>
                <textarea
                  name="markSchemeAnswer"
                  placeholder="Enter mark scheme here (Optional if image uploaded)"
                  value={newQuestion.markSchemeAnswer}
                  onChange={handleInputChange}
                  className="textarea-field"
                />
              </div>
            </div>

            {/* Image Upload Row */}
            <div className="image-upload-container">
              {/* Question Image Upload */}
              <div>
                <label className="form-label">
                  Upload Question Image (Optional)
                </label>
                <div className="upload-button-container">
                  <label
                    htmlFor="questionImageInput"
                    className="file-browser-button"
                  >
                    Browse Files
                  </label>
                  <span className="file-name">
                    {questionImage ? questionImage.name : "No file selected"}
                  </span>
                </div>
                <input
                  id="questionImageInput"
                  type="file"
                  accept="image/*"
                  onChange={handleQuestionFileChange}
                  style={{ display: "none" }}
                />
                <button
                  type="button"
                  onClick={uploadQuestionImage}
                  disabled={!questionImage}
                  className="file-browser-button"
                  style={{
                    marginTop: "8px",
                    opacity: questionImage ? 1 : 0.6,
                    cursor: questionImage ? "pointer" : "not-allowed",
                  }}
                >
                  Upload Question Image
                </button>
                {questionImageURL && (
                  <div style={{ marginTop: "8px" }}>
                    <img
                      src={questionImageURL}
                      alt="Question Preview"
                      className="image-preview"
                    />
                  </div>
                )}
              </div>

              {/* Mark Scheme Image Upload */}
              <div>
                <label className="form-label">
                  Upload Mark Scheme Image (Optional)
                </label>
                <div className="upload-button-container">
                  <label
                    htmlFor="markSchemeImageInput"
                    className="file-browser-button"
                  >
                    Browse Files
                  </label>
                  <span className="file-name">
                    {markSchemeImage
                      ? markSchemeImage.name
                      : "No file selected"}
                  </span>
                </div>
                <input
                  id="markSchemeImageInput"
                  type="file"
                  accept="image/*"
                  onChange={handleMarkSchemeFileChange}
                  style={{ display: "none" }}
                />
                <button
                  type="button"
                  onClick={uploadMarkSchemeImage}
                  disabled={!markSchemeImage}
                  className="file-browser-button"
                  style={{
                    marginTop: "8px",
                    opacity: markSchemeImage ? 1 : 0.6,
                    cursor: markSchemeImage ? "pointer" : "not-allowed",
                  }}
                >
                  Upload Mark Scheme Image
                </button>
                {markSchemeImageURL && (
                  <div style={{ marginTop: "8px" }}>
                    <img
                      src={markSchemeImageURL}
                      alt="Mark Scheme Preview"
                      className="image-preview"
                    />
                  </div>
                )}
              </div>
            </div>

            {/* Additional Fields Row */}
            <div className="form-fields-container">
              <div>
                <label className="form-label">
                  Max Mark *
                </label>
                <input
                  type="number"
                  name="maxMark"
                  placeholder="e.g. 5"
                  value={newQuestion.maxMark}
                  onChange={handleInputChange}
                  required
                  className="form-input"
                />
              </div>

              <div>
                <label className="form-label">
                  Topic *
                </label>
                <input
                  type="text"
                  name="topic"
                  placeholder="e.g. Photosynthesis"
                  value={newQuestion.topic}
                  onChange={handleInputChange}
                  required
                  className="form-input"
                />
              </div>

              <div>
                <label className="form-label">
                  Subject *
                </label>
                <input
                  type="text"
                  name="subject"
                  placeholder="e.g. Biology"
                  value={newQuestion.subject}
                  onChange={handleInputChange}
                  required
                  className="form-input"
                />
              </div>

              <div>
                <label className="form-label">
                  Level *
                </label>
                <input
                  type="text"
                  name="level"
                  placeholder="e.g. GCSE"
                  value={newQuestion.level}
                  onChange={handleInputChange}
                  required
                  className="form-input"
                />
              </div>

              <div>
                <label className="form-label">
                  Spec Code *
                </label>
                <input
                  type="text"
                  name="specCode"
                  placeholder="e.g. AQA-BIO-4.1"
                  value={newQuestion.specCode}
                  onChange={handleInputChange}
                  required
                  className="form-input"
                />
              </div>

              <div>
                <label className="form-label">
                  Answer Input Size
                </label>
                <select
                  name="textBoxSize"
                  value={newQuestion.textBoxSize}
                  onChange={handleInputChange}
                  className="form-select"
                >
                  <option value="small">Small</option>
                  <option value="medium">Medium</option>
                  <option value="large">Large</option>
                </select>
              </div>
            </div>

            <div className="form-submit-container">
              <button
                type="submit"
                disabled={isSubmitting}
                className="submit-button"
                style={{ opacity: isSubmitting ? 0.7 : 1 }}
              >
                {isSubmitting ? "Saving..." : "Save Question"}
              </button>
              <span className="required-field-note">
                * Required fields
              </span>
            </div>
          </form>
        </div>
      )}

      {/* Subject Selection Section */}
      <div className="subject-section">
        <h3 className="subject-title">
          Select a Subject
        </h3>
        <div className="subject-buttons">
          {subjects.map((subject) => (
            <button
              key={subject}
              onClick={() => handleSubjectSelect(subject)}
              className={`subject-button ${selectedSubject === subject ? 'selected' : ''}`}
            >
              {subject}
            </button>
          ))}
        </div>
      </div>

      {/* Filters Section - Only show if subject is selected */}
      {selectedSubject && (
        <div className="filter-section">
          <h3 className="filter-title">
            Filter Questions
          </h3>
          <div className="filter-container">
            <div>
              <label className="filter-label">
                Topic
              </label>
              <input
                type="text"
                name="topic"
                placeholder="Filter by Topic"
                value={filters.topic}
                onChange={handleFilterChange}
                className="filter-input"
              />
            </div>
            <div>
              <label className="filter-label">
                Level
              </label>
              <input
                type="text"
                name="level"
                placeholder="Filter by Level"
                value={filters.level}
                onChange={handleFilterChange}
                className="filter-input"
              />
            </div>
            <div>
              <label className="filter-label">
                Spec Code
              </label>
              <input
                type="text"
                name="specCode"
                placeholder="Filter by Spec Code"
                value={filters.specCode}
                onChange={handleFilterChange}
                className="filter-input"
              />
            </div>
            <button
              onClick={clearFilters}
              className="clear-button"
            >
              Clear Filters
            </button>
          </div>
        </div>
      )}

      {/* Questions Table Section - Only show if subject is selected */}
      {selectedSubject && (
        <div style={{ marginBottom: "40px" }}>
          {/* Table Header with Actions */}
          <div className="table-header">
            <h2 className="table-title">
              Questions for {selectedSubject}
            </h2>
            
            {selectedQuestionIds.length > 0 && (
              <button
                onClick={handleDeleteSelected}
                className="delete-button"
              >
                Delete Selected ({selectedQuestionIds.length})
              </button>
            )}
          </div>

          {/* Questions Table */}
          {filteredQuestions.length > 0 ? (
            <div style={{ overflowX: "auto" }}>
              <table className="questions-table">
                <thead className="table-head">
                  <tr>
                    <th className="table-head-cell" style={{ width: "40px" }}>
                      Select
                    </th>
                    <th className="table-head-cell">Question</th>
                    <th className="table-head-cell">Mark Scheme</th>
                    <th className="table-head-cell">Topic</th>
                    <th className="table-head-cell">Level</th>
                    <th className="table-head-cell">Spec Code</th>
                    <th className="table-head-cell">Max Mark</th>
                    <th className="table-head-cell">Actions</th>
                  </tr>
                </thead>
                <tbody>
                  {filteredQuestions.map((question) => (
                    <tr
                      key={question.id}
                      className={`table-row ${selectedQuestionIds.includes(question.id) ? 'selected' : ''}`}
                    >
                      <td className="table-cell-center">
                        <input
                          type="checkbox"
                          checked={selectedQuestionIds.includes(question.id)}
                          onChange={() => handleCheckboxChange(question.id)}
                        />
                      </td>
                      <td className="table-cell-content">
                        {question.questionText && (
                          <div className="cell-text">
                            {question.questionText}
                          </div>
                        )}
                        {question.questionImageURL && (
                          <img
                            src={question.questionImageURL}
                            alt="Question"
                            className="cell-image"
                          />
                        )}
                      </td>
                      <td className="table-cell-content">
                        {question.markSchemeAnswer && (
                          <div className="cell-text">
                            {question.markSchemeAnswer}
                          </div>
                        )}
                        {question.markSchemeImageURL && (
                          <img
                            src={question.markSchemeImageURL}
                            alt="Mark Scheme"
                            className="cell-image"
                          />
                        )}
                      </td>
                      <td className="table-cell">
                        {question.topic}
                      </td>
                      <td className="table-cell">
                        {question.level}
                      </td>
                      <td className="table-cell">
                        {question.specCode}
                      </td>
                      <td className="table-cell">
                        {question.maxMark}
                      </td>
                      <td className="table-cell-center">
                        <Link to={`/edit-question/${question.id}`} className="edit-button">
                          Edit
                        </Link>
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          ) : (
            <div className="no-questions-message">
              <p className="no-questions-text">
                No questions found for the selected filters.
              </p>
            </div>
          )}
        </div>
      )}
    </div>
  );
};

export default CreateQuestions;
